import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Stack, IconButton, Slider, Typography, Tooltip } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import BrushIcon from '@mui/icons-material/Brush';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import api from '../../services/api';

type Tool = 'pen' | 'arrow';

// Draw a straight arrow (shaft + filled triangular arrowhead) on `ctx`.
function drawArrow(
  ctx: CanvasRenderingContext2D,
  fromX: number, fromY: number,
  toX: number, toY: number,
  color: string, width: number,
) {
  const headLen = Math.max(width * 4, 12);
  const angle = Math.atan2(toY - fromY, toX - fromX);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Shaft — stop short of the tip so the head sits cleanly
  const shaftEndX = toX - (headLen * 0.6) * Math.cos(angle);
  const shaftEndY = toY - (headLen * 0.6) * Math.sin(angle);
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(shaftEndX, shaftEndY);
  ctx.stroke();
  // Filled arrowhead
  ctx.beginPath();
  ctx.moveTo(toX, toY);
  ctx.lineTo(
    toX - headLen * Math.cos(angle - Math.PI / 6),
    toY - headLen * Math.sin(angle - Math.PI / 6),
  );
  ctx.lineTo(
    toX - headLen * Math.cos(angle + Math.PI / 6),
    toY - headLen * Math.sin(angle + Math.PI / 6),
  );
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

interface ImageAnnotatorProps {
  imageUrl: string;
  proxyUrl?: string; // unused now, kept for interface compat
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
}

const COLORS = ['#ff0000', '#00ff00', '#ffff00', '#00bfff', '#ff69b4', '#ffffff'];
const MAX_WIDTH = 500;

export default function ImageAnnotator({ imageUrl, proxyUrl, onSave, onCancel }: ImageAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#ff0000');
  const [lineWidth, setLineWidth] = useState(4);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [history, setHistory] = useState<ImageData[]>([]);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const scaleRef = useRef(1);
  // Arrow-tool live-preview state — start point + snapshot of canvas before this stroke
  const arrowStartRef = useRef<{ x: number; y: number } | null>(null);
  const arrowSnapshotRef = useRef<ImageData | null>(null);

  const [loadError, setLoadError] = useState(false);

  // Load image onto canvas — fetch via authenticated API proxy for external URLs
  useEffect(() => {
    let cancelled = false;
    setImgLoaded(false);
    setLoadError(false);

    const loadImage = async () => {
      const isExternal = imageUrl.startsWith('http') && !imageUrl.includes(window.location.hostname);
      const isDataUrl = imageUrl.startsWith('data:');
      let blobUrl: string | null = null;

      try {
        let src = imageUrl;
        if (isExternal) {
          // Fetch through authenticated backend proxy to avoid CORS canvas taint
          const resp = await api.get('/api/products/proxy-image', {
            params: { url: imageUrl },
            responseType: 'blob',
          });
          blobUrl = URL.createObjectURL(resp.data);
          src = blobUrl;
        }

        const img = new Image();
        // Only set crossOrigin for external http URLs loaded via blob; skip for data URLs and local
        if (!isDataUrl) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => {
          if (cancelled) { if (blobUrl) URL.revokeObjectURL(blobUrl); return; }
          imgRef.current = img;
          const canvas = canvasRef.current;
          if (!canvas) return;
          const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
          scaleRef.current = scale;
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setHistory([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
          setImgLoaded(true);
          setLoadError(false);
        };
        img.onerror = (e) => {
          console.error('Image load error for annotation:', imageUrl.substring(0, 100), e);
          if (!cancelled) setLoadError(true);
          if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
        img.src = src;
      } catch (err) {
        console.error('Failed to load image for annotation:', err);
        if (!cancelled) setLoadError(true);
      }
    };
    loadImage();
    return () => { cancelled = true; };
  }, [imageUrl]);

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }, []);

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    const pos = getPos(e);
    if (tool === 'pen') {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    } else {
      // Arrow: remember start + snapshot canvas so move events can repaint a live preview
      arrowStartRef.current = pos;
      arrowSnapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    }
  }, [tool, color, lineWidth, getPos]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    if (tool === 'pen') {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (arrowStartRef.current && arrowSnapshotRef.current) {
      // Restore pre-stroke canvas, then paint preview arrow at new endpoint
      ctx.putImageData(arrowSnapshotRef.current, 0, 0);
      drawArrow(ctx, arrowStartRef.current.x, arrowStartRef.current.y, pos.x, pos.y, color, lineWidth);
    }
  }, [isDrawing, tool, color, lineWidth, getPos]);

  const endDraw = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (tool === 'pen') {
      ctx.closePath();
    } else {
      arrowStartRef.current = null;
      arrowSnapshotRef.current = null;
    }
    // Save state for undo
    setHistory(prev => [...prev, ctx.getImageData(0, 0, canvas.width, canvas.height)]);
  }, [isDrawing, tool]);

  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const newHistory = history.slice(0, -1);
    ctx.putImageData(newHistory[newHistory.length - 1], 0, 0);
    setHistory(newHistory);
  }, [history]);

  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
      setSaving(true);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      await onSave(dataUrl);
    } catch (err) {
      console.error('Save failed:', err);
      setLoadError(true);
      setSaving(false);
    }
  }, [onSave]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', maxWidth: MAX_WIDTH, flexWrap: 'wrap' }}>
        {/* Tool toggles */}
        <Tooltip title="自由畫">
          <IconButton size="small" onClick={() => setTool('pen')}
            sx={{
              color: tool === 'pen' ? '#f59e0b' : 'rgba(255,255,255,0.7)',
              bgcolor: tool === 'pen' ? 'rgba(245,158,11,0.15)' : 'transparent',
              border: tool === 'pen' ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
            }}>
            <BrushIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="畫箭頭">
          <IconButton size="small" onClick={() => setTool('arrow')}
            sx={{
              color: tool === 'arrow' ? '#f59e0b' : 'rgba(255,255,255,0.7)',
              bgcolor: tool === 'arrow' ? 'rgba(245,158,11,0.15)' : 'transparent',
              border: tool === 'arrow' ? '1px solid #f59e0b' : '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
            }}>
            <ArrowRightAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Color swatches */}
        {COLORS.map(c => (
          <Box key={c} onClick={() => setColor(c)}
            sx={{
              width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
              border: color === c ? '3px solid #fff' : '2px solid rgba(255,255,255,0.3)',
              boxShadow: color === c ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
              transition: 'all 0.15s',
            }} />
        ))}
        <Box sx={{ flex: 1, minWidth: 60, mx: 1 }}>
          <Slider value={lineWidth} onChange={(_, v) => setLineWidth(v as number)}
            min={2} max={12} step={1} size="small"
            sx={{ color: 'rgba(255,255,255,0.7)', '& .MuiSlider-thumb': { width: 14, height: 14 } }} />
        </Box>
        <IconButton onClick={handleUndo} disabled={history.length <= 1}
          sx={{ color: 'rgba(255,255,255,0.7)', '&:disabled': { color: 'rgba(255,255,255,0.2)' } }}>
          <UndoIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Canvas */}
      <Box sx={{ position: 'relative', maxWidth: '78vw', touchAction: 'none' }}>
        <canvas ref={canvasRef}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
          style={{
            maxWidth: '100%', height: 'auto', borderRadius: '10px', cursor: 'crosshair',
            display: imgLoaded ? 'block' : 'none',
          }} />
        {!imgLoaded && !loadError && (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', py: 8 }}>載入圖片中...</Typography>
        )}
        {loadError && (
          <Typography sx={{ color: '#ef4444', py: 4, textAlign: 'center' }}>
            圖片載入失敗，請改用「設為參考圖」直接設定
          </Typography>
        )}
      </Box>

      {/* Action buttons */}
      <Stack direction="row" spacing={1.5}>
        <Button variant="outlined" onClick={onCancel} startIcon={<CloseIcon />}
          sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600 }}>
          取消
        </Button>
        <Button variant="contained" onClick={handleSave} startIcon={<CheckIcon />} disabled={!imgLoaded || saving}
          sx={{ bgcolor: '#f59e0b', color: '#fff', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#d97706' } }}>
          {saving ? '儲存中...' : '確認儲存'}
        </Button>
      </Stack>
    </Box>
  );
}
